<div class="system-status">
  <header class="system-status__header">
    <div>
      <h5>{{ 'platform.systemStatus.title' | transloco }}</h5>
      @if (status(); as current) {
        <p class="system-status__hint" data-test-id="system-status-checked-at">
          {{ 'platform.systemStatus.checkedAt' | transloco: { time: (current.checkedAtUtc | dateTimeFormat: 'readableMetricDateAndTime') } }}
        </p>
      }
    </div>
    <button
      eBtn
      size="sm"
      color="highlight"
      icon="arrows-rotate"
      [loading]="isLoading()"
      data-test-id="system-status-refresh"
      (click)="refresh()">
      {{ 'platform.systemStatus.refresh' | transloco }}
    </button>
  </header>

  @if (loadFailed()) {
    <p class="system-status__error" data-test-id="system-status-load-failed">{{ 'platform.systemStatus.loadFailed' | transloco }}</p>
  }

  @if (status(); as current) {
    <div class="system-status__cards">
      <!-- UptimeRobot -->
      <section class="system-status__card" [attr.data-tone]="uptimeRobotTone()" data-test-id="system-status-uptimerobot">
        <ng-container
          [ngTemplateOutlet]="cardHeader"
          [ngTemplateOutletContext]="{ title: 'platform.systemStatus.uptimeRobot.title', subtitle: 'platform.systemStatus.uptimeRobot.subtitle', tone: uptimeRobotTone(), url: uptimeRobotDashboardUrl }">
        </ng-container>

        @if (current.uptimeRobot?.state === 'Ok') {
          <ul class="system-status__rows">
            @for (monitor of current.uptimeRobot?.monitors ?? []; track monitor.id) {
              <li class="system-status__row" data-test-id="system-status-monitor">
                <div class="system-status__row-main">
                  <span class="system-status__dot" [attr.data-tone]="monitorTone(monitor.status)"></span>
                  <span class="system-status__name">{{ monitor.name }}</span>
                  <span class="system-status__value">{{ 'platform.systemStatus.uptimeRobot.monitorStatus.' + monitor.status | transloco }}</span>
                </div>
                <div class="system-status__row-detail">
                  <span>
                    {{ 'platform.systemStatus.uptimeRobot.ratios' | transloco: {
                      ratio7: monitor.uptimeRatio7Days === null || monitor.uptimeRatio7Days === undefined ? '-' : (monitor.uptimeRatio7Days | number: '1.0-3') + '%',
                      ratio30: monitor.uptimeRatio30Days === null || monitor.uptimeRatio30Days === undefined ? '-' : (monitor.uptimeRatio30Days | number: '1.0-3') + '%'
                    } }}
                  </span>
                  @if (monitor.lastIncident; as incident) {
                    <span data-test-id="system-status-incident">
                      {{ 'platform.systemStatus.uptimeRobot.lastIncident' | transloco: {
                        date: (incident.startedAtUtc | dateTimeFormat: 'readableMetricDateAndTime'),
                        duration: formatDuration(incident.durationSeconds)
                      } }}
                      @if (incident.reason) {
                        · {{ incident.reason }}
                      }
                    </span>
                  } @else {
                    <span>{{ 'platform.systemStatus.uptimeRobot.noIncident' | transloco }}</span>
                  }
                </div>
              </li>
            } @empty {
              <li class="system-status__hint">{{ 'platform.systemStatus.uptimeRobot.noMonitors' | transloco }}</li>
            }
          </ul>
        } @else {
          <ng-container
            [ngTemplateOutlet]="sourceProblem"
            [ngTemplateOutletContext]="{ state: current.uptimeRobot?.state, detail: current.uptimeRobot?.detail, envKey: 'UPTIMEROBOT_API_KEY' }">
          </ng-container>
        }
      </section>

      <!-- Healthchecks.io: nachtelijke back-up -->
      <section class="system-status__card" [attr.data-tone]="healthchecksTone()" data-test-id="system-status-healthchecks">
        <ng-container
          [ngTemplateOutlet]="cardHeader"
          [ngTemplateOutletContext]="{ title: 'platform.systemStatus.healthchecks.title', subtitle: 'platform.systemStatus.healthchecks.subtitle', tone: healthchecksTone(), url: healthchecksDashboardUrl }">
        </ng-container>

        @if (current.healthchecks?.state === 'Ok' && current.healthchecks?.check; as check) {
          <dl class="system-status__facts">
            <dt>{{ 'platform.systemStatus.healthchecks.statusLabel' | transloco }}</dt>
            <dd data-test-id="system-status-backup-status">{{ 'platform.systemStatus.healthchecks.status.' + check.status | transloco }}</dd>
            <dt>{{ 'platform.systemStatus.healthchecks.lastPing' | transloco }}</dt>
            <dd>
              @if (check.lastPingUtc) {
                {{ check.lastPingUtc | dateTimeFormat: 'readableMetricDateAndTime' }}
              } @else {
                {{ 'platform.systemStatus.healthchecks.never' | transloco }}
              }
            </dd>
            <dt>{{ 'platform.systemStatus.healthchecks.nextPing' | transloco }}</dt>
            <dd>
              @if (check.nextPingUtc) {
                {{ check.nextPingUtc | dateTimeFormat: 'readableMetricDateAndTime' }}
              } @else {
                {{ 'platform.systemStatus.healthchecks.none' | transloco }}
              }
            </dd>
          </dl>
        } @else {
          <ng-container
            [ngTemplateOutlet]="sourceProblem"
            [ngTemplateOutletContext]="{ state: current.healthchecks?.state, detail: current.healthchecks?.detail, envKey: 'HEALTHCHECKS_API_KEY' }">
          </ng-container>
        }
      </section>

      <!-- Eigen health checks -->
      <section class="system-status__card" [attr.data-tone]="platformTone()" data-test-id="system-status-platform">
        <ng-container
          [ngTemplateOutlet]="cardHeader"
          [ngTemplateOutletContext]="{ title: 'platform.systemStatus.platform.title', subtitle: 'platform.systemStatus.platform.subtitle', tone: platformTone() }">
        </ng-container>

        @if (current.platform?.state === 'Ok') {
          <ul class="system-status__rows">
            @for (entry of current.platform?.entries ?? []; track entry.name) {
              <li class="system-status__row" data-test-id="system-status-health-entry">
                <div class="system-status__row-main">
                  <span class="system-status__dot" [attr.data-tone]="healthTone(entry.status)"></span>
                  <span class="system-status__name">{{ entry.name }}</span>
                  <span class="system-status__value">{{ 'platform.systemStatus.platform.status.' + entry.status | transloco }}</span>
                </div>
                @if (entry.description) {
                  <div class="system-status__row-detail">{{ entry.description }}</div>
                }
              </li>
            }
          </ul>
        } @else {
          <p class="system-status__problem">
            {{ 'platform.systemStatus.platform.failed' | transloco }}
            @if (current.platform?.detail) {
              <span class="system-status__detail">{{ current.platform?.detail }}</span>
            }
          </p>
        }
      </section>
    </div>
  } @else if (isLoading()) {
    <p class="system-status__hint">{{ 'platform.systemStatus.loading' | transloco }}</p>
  }
</div>

<ng-template #cardHeader let-title="title" let-subtitle="subtitle" let-tone="tone" let-url="url">
  <div class="system-status__card-header">
    <div class="system-status__card-title">
      <span class="system-status__dot system-status__dot--large" [attr.data-tone]="tone"></span>
      <div>
        <h6>{{ title | transloco }}</h6>
        <span class="system-status__hint">{{ subtitle | transloco }}</span>
      </div>
    </div>
    @if (url) {
      <a class="system-status__open" [href]="url" target="_blank" rel="noopener noreferrer" data-test-id="system-status-open">
        <i eIcon icon="arrow-up-right-from-square"></i> {{ 'platform.systemStatus.open' | transloco }}
      </a>
    }
  </div>
</ng-template>

<ng-template #sourceProblem let-state="state" let-detail="detail" let-envKey="envKey">
  <div class="system-status__problem" data-test-id="system-status-problem" [attr.data-state]="state">
    <strong>{{ 'platform.systemStatus.state.' + state | transloco }}</strong>
    <span>{{ 'platform.systemStatus.stateHint.' + state | transloco: { key: envKey } }}</span>
    @if (detail) {
      <span class="system-status__detail">{{ detail }}</span>
    }
  </div>
</ng-template>
